'use client';

import { createStore } from 'zustand';

import { getLogger } from '@/logger/logger';
import { getOpenPayments } from '@/services/open-payments/get-open-payments.service';
import { OpenPayment } from '@/types/open-payment.types';

const logger = getLogger('open-payments.store');

export type OpenPaymentsFetchStatus = 'idle' | 'loading' | 'loaded' | 'error';

export type OpenPaymentsLoadOutcome = 'loaded' | 'aborted' | 'failed';

export type OpenPaymentsState = {
  payments: OpenPayment[];
  status: OpenPaymentsFetchStatus;
  isNoticeDismissed: boolean;
  hasTrackedNoticeView: boolean;
};

const initialState: OpenPaymentsState = {
  payments: [],
  status: 'idle',
  isNoticeDismissed: false,
  hasTrackedNoticeView: false,
};

export const openPaymentsStore = createStore<OpenPaymentsState>(() => ({ ...initialState }));

let pendingLoad: Promise<OpenPaymentsLoadOutcome> | null = null;
let pendingLoadAbort: AbortController | null = null;

export function loadOpenPayments(): Promise<OpenPaymentsLoadOutcome> {
  if (pendingLoad) {
    return pendingLoad;
  }

  const abortController = new AbortController();
  const { signal } = abortController;

  pendingLoadAbort = abortController;

  if (openPaymentsStore.getState().status !== 'loaded') {
    openPaymentsStore.setState({ status: 'loading' });
  }

  const load = getOpenPayments(signal)
    .then((payments): OpenPaymentsLoadOutcome => {
      if (signal.aborted) {
        return 'aborted';
      }

      openPaymentsStore.setState({ payments, status: 'loaded' });

      return 'loaded';
    })
    .catch((error): OpenPaymentsLoadOutcome => {
      if (signal.aborted) {
        return 'aborted';
      }

      logger.error('Failed to fetch the open payments', {
        open_payments: { event: 'fetch_failed' },
        error,
      });
      openPaymentsStore.setState({ status: 'error' });

      return 'failed';
    })
    .finally(() => {
      if (pendingLoad === load) {
        pendingLoad = null;
        pendingLoadAbort = null;
      }
    });

  pendingLoad = load;

  return load;
}

export function dismissOpenPaymentsNotice(): void {
  openPaymentsStore.setState({ isNoticeDismissed: true });
}

export function markOpenPaymentsNoticeViewed(): void {
  openPaymentsStore.setState({ hasTrackedNoticeView: true });
}

export function resetOpenPayments(): void {
  pendingLoadAbort?.abort();
  pendingLoad = null;
  pendingLoadAbort = null;
  openPaymentsStore.setState({ ...initialState });
}
